import type { NextPage } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { Experiments } from '@src/constants/abTest';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { ListKeys } from '@src/constants/microcredits';
import ProductList from '@src/containers/list/ProductList';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { useLoadRestOffers } from '@src/hooks/useLoadRestOffers';
import { useTestVariant } from '@src/hooks/useTestVariants';
import { useSelectUser } from '@src/reducers/user';
import { isMainPage } from '@src/utils/routing';
import { Layout as AppLayout } from 'app/Layout';
import { fullDealOfferAction, sravniOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider/CardActionProvider';
import { AnchorLinkList } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';
import { SeoComponents } from 'widgets/SeoComponents';

import { getFilterMode, useGetHeaderFilters } from '../lib';

import styles from './ListPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.LIST,
  source: 'search',
  sortPanelVisible: true,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
  isMainPage: true,
};

export const ListPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname, hasUtmLabel, isMainMicroCreditsPage } = useListPage(ListKeys.LIST);
  const user = useSelectUser();
  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);

  const { isBVariant } = useTestVariant(Experiments.SEA_OFFERS_LIMIT);
  const isSEAOffersLimitExperimentActive = hasUtmLabel && isBVariant && isMainMicroCreditsPage;

  useLoadRestOffers(ListKeys.LIST, !isSEAOffersLimitExperimentActive);
  useBestProducts();

  const { Filters } = useGetHeaderFilters(filter, seo, user);

  return (
    <AppLayout>
      <PageContext.Provider value={config}>
        <BrandingHeaders containerClassName={styles.page_header} afterHeaders={Filters} />

        {!hasUtmLabel && <AnchorLinkList />}

        <MobileSubHeader />

        <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
          <ProductList
            className={styles.product_list}
            gridLayout
            filter={filter}
            pathname={pathname}
            showMoreButton={isSEAOffersLimitExperimentActive ? <div /> : undefined}
            renderCard={(props) => <LightweightCard hasUtmLabel={hasUtmLabel} {...props} />}
          />
        </CardActionProvider>
        {seo && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={false}
            showSubheader={false}
          />
        )}
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE, BottomBannerTypes.SIDEBAR]} />
      </PageContext.Provider>
    </AppLayout>
  );
};

ListPage.getInitialProps = (ctx: Application.ReduxNextPageContext) => {
  const isSEAOffersLimitExperimentActive =
    isMainPage(ctx.asPath, {}) &&
    ctx.store.getState()?.metadata?.abTestingInfo?.experiments?.[Experiments.SEA_OFFERS_LIMIT] === '1';

  const filterMode = getFilterMode(ctx, isSEAOffersLimitExperimentActive);

  return getPageListData(ctx, {
    key: ListKeys.LIST,
    defaultFiltersMode: filterMode,
    isSEAOffersLimitExperimentActive,
  });
};
